// The upload row on /bestsellers → Upload bestsellers.
//
// A file picker, a drop zone and the thumbnails of what has been uploaded so far. The copy is
// load-bearing: the tab's whole value is that it takes bestsellers which are NOT on Pictufy,
// and a reseller who assumes otherwise will not use it.
import { useEffect, useState } from "preact/hooks";
import { Islands } from "@brave-new-world/shared";
import { mountIsland } from "../../ui/islands";
import {
  MAX_UPLOADS,
  addUploads,
  clearUploads,
  getUploads,
  removeUpload,
  subscribeUploads,
  type Upload,
} from "./uploads";
import { loadCurrentTab } from "./tabs";

function UploadStrip() {
  const [uploads, setUploads] = useState<Upload[]>(getUploads());
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [input, setInput] = useState<HTMLInputElement | null>(null);

  useEffect(() => subscribeUploads(setUploads), []);

  /**
   * Embed the files, then re-run the search.
   *
   * Re-run once at the end rather than per file: each one is a clustered kNN costing ~0.8 s,
   * and running it per file would both thrash the database and reshuffle the grid under
   * somebody who is still picking images.
   */
  const take = (files: File[]): void => {
    if (files.length === 0) return;
    setBusy(true);
    void addUploads(files)
      .then((added) => {
        if (added > 0) loadCurrentTab();
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div class="bestsellers-upload">
      <div class="bestsellers-upload-intro">
        <b>Upload your own bestsellers</b> to find similar artworks in the Pictufy catalogue.
        {/* The sentence the tab exists for. */}
        <br />
        They do not have to be Pictufy artworks — upload anything that sells well for you, and we
        will look for the closest matches we have.
      </div>

      <div
        class={dragging ? "bestsellers-dropzone bestsellers-dropzone-over" : "bestsellers-dropzone"}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          take([...(e.dataTransfer?.files ?? [])]);
        }}
      >
        <input
          ref={setInput}
          type="file"
          accept="image/*"
          multiple
          style="display:none"
          onChange={(e) => {
            const files = [...(e.currentTarget.files ?? [])];
            // Reset first, so picking the same file twice still fires a change.
            e.currentTarget.value = "";
            take(files);
          }}
        />
        <span
          class="button medium lilac"
          onClick={() => {
            if (!busy) input?.click();
          }}
        >
          <i class={busy ? "fas fa-spinner fa-spin" : "fas fa-upload"} />{" "}
          {busy ? "Reading images" : "Choose images"}
        </span>
        <span class="bestsellers-dropzone-hint">
          {" "}
          or drop them here — up to {String(MAX_UPLOADS)}
        </span>
      </div>

      {uploads.length > 0 && (
        <div class="bestsellers-uploads">
          {uploads.map((u) => (
            <span key={u.id} class="bestsellers-upload-thumb" title={u.name}>
              <img src={u.preview} alt="" />
              <span
                class="bestsellers-upload-remove"
                title="Remove this image"
                onClick={() => {
                  removeUpload(u.id);
                  // Re-run immediately: the results no longer reflect what is on screen, and
                  // leaving them would be the one state where the grid lies about its input.
                  loadCurrentTab();
                }}
              >
                <i class="fas fa-xmark" />
              </span>
            </span>
          ))}
          <span
            class="button small grey bestsellers-upload-clear"
            onClick={() => {
              clearUploads();
              loadCurrentTab();
            }}
          >
            Clear all
          </span>
        </div>
      )}
    </div>
  );
}

export function initBestsellerUploads(): void {
  mountIsland(Islands.bestsellerUploads, UploadStrip, {});
}
